import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtDate } from "@/lib/format";
import { useCurrency } from "@/lib/currency";
import { ReceiptText, ClipboardList, Truck, Sparkles, TrendingUp } from "lucide-react";

export default function EntityDetailDrawer({ open, onOpenChange, entityType, entityId }) {
  const { fmtMoney } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entityId) return;
    setLoading(true);
    const url = entityType === "vendor" ? `/vendors/${entityId}/summary` : `/agencies/${entityId}/summary`;
    api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [open, entityId, entityType]);

  const entity = data?.vendor || data?.agency;
  const isVendor = entityType === "vendor";
  const Icon = isVendor ? Truck : Sparkles;
  const color = isVendor ? "#B75B50" : "#A18E9F";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid={`${entityType}-drawer`}
        side="right"
        className="w-full sm:max-w-[540px] bg-white p-0 overflow-y-auto border-black/[0.05]"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/[0.05]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${color}18`, color }}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#8E8E93] font-medium">
                {isVendor ? "Vendor" : "Agency"} {entity?.category || entity?.type ? `· ${entity?.category || entity?.type}` : ""}
              </div>
              <SheetTitle className="font-display font-semibold text-[20px] tracking-tight truncate">
                {entity?.name || (loading ? "Loading…" : "—")}
              </SheetTitle>
              {entity?.email && <div className="text-[12px] text-[#8E8E93] mt-0.5">{entity.email}</div>}
            </div>
          </div>
        </SheetHeader>

        {loading && (
          <div className="p-6 space-y-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-2xl shimmer" />)}
          </div>
        )}

        {!loading && data && (
          <div className="p-6 space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-[#FAFAFA] border border-black/[0.04] p-4">
                <div className="text-[10.5px] uppercase tracking-wider text-[#8E8E93] font-medium">Total spend</div>
                <div className="mt-1 font-display font-semibold text-[22px]">{fmtMoney(entity?.total_spend || data.expense_total)}</div>
                <div className="text-[11px] text-[#8E8E93] mt-1">Manual + roll-ups</div>
              </div>
              {isVendor ? (
                <div className="rounded-2xl bg-[#FAFAFA] border border-black/[0.04] p-4">
                  <div className="text-[10.5px] uppercase tracking-wider text-[#8E8E93] font-medium">Purchase orders</div>
                  <div className="mt-1 font-display font-semibold text-[22px]">{data.purchase_orders?.length || 0}</div>
                  <div className="text-[11px] text-[#8E8E93] mt-1">{fmtMoney(data.po_total)} committed</div>
                </div>
              ) : (
                <div className="rounded-2xl bg-[#FAFAFA] border border-black/[0.04] p-4">
                  <div className="text-[10.5px] uppercase tracking-wider text-[#8E8E93] font-medium">Attributed revenue</div>
                  <div className="mt-1 font-display font-semibold text-[22px] text-[#34C759]">{fmtMoney(entity?.attributed_revenue)}</div>
                  <div className="text-[11px] text-[#8E8E93] mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {data.roas ? `${data.roas}x ROAS` : "no ROAS yet"}
                  </div>
                </div>
              )}
            </div>

            {/* Linked expenses ledger */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ReceiptText className="w-4 h-4 text-[#A9684E]" />
                <div className="font-display font-semibold text-[14px]">Linked expenses</div>
                <span className="text-[11px] text-[#8E8E93]">({data.expenses?.length || 0})</span>
              </div>
              {(!data.expenses || data.expenses.length === 0) ? (
                <div className="text-[12.5px] text-[#8E8E93] py-3">No expenses linked yet. Assign an expense to this {entityType} from the Expenses page.</div>
              ) : (
                <div className="rounded-2xl border border-black/[0.04] divide-y divide-black/[0.04]">
                  {data.expenses.map((e) => (
                    <div key={e.id} data-testid={`ledger-exp-${e.id}`} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium truncate">{e.description}</div>
                        <div className="text-[11px] text-[#8E8E93] mt-0.5">{e.category} · {fmtDate(e.date)}</div>
                      </div>
                      <div className="font-mono font-semibold text-[#FF3B30]">-{fmtMoney(e.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Purchase orders (vendor only) */}
            {isVendor && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardList className="w-4 h-4 text-[#FF9500]" />
                  <div className="font-display font-semibold text-[14px]">Purchase orders</div>
                  <span className="text-[11px] text-[#8E8E93]">({data.purchase_orders?.length || 0})</span>
                </div>
                {(!data.purchase_orders || data.purchase_orders.length === 0) ? (
                  <div className="text-[12.5px] text-[#8E8E93] py-3">No POs for this vendor yet.</div>
                ) : (
                  <div className="rounded-2xl border border-black/[0.04] divide-y divide-black/[0.04]">
                    {data.purchase_orders.map((p) => (
                      <div key={p.id} className="flex items-center justify-between px-4 py-3">
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium">{p.po_number}</div>
                          <div className="text-[11px] text-[#8E8E93] mt-0.5 capitalize">{p.status} · {p.lines?.length || 0} item(s)</div>
                        </div>
                        <div className="font-mono font-semibold">{fmtMoney(p.total)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
